import React from 'react';
import { Home, Download, LogOut, Video, Lightbulb, X, Film, Music, GraduationCap, Heart, Package, Wand2, Layers, Lock } from 'lucide-react';
import { cn } from '../lib/utils';

const Sidebar = ({ 
  user, 
  collections, 
  activeView, 
  setActiveView, 
  selectedCollection, 
  setSelectedCollection, 
  onLogout, 
  isOpen, 
  setIsOpen,
  packs = [],
  favorites = [],
  hasSFXAccess = false,
  hasSFXPlan = false,
  sfxDaysRemaining = 0
}) => {

  const handleNavigation = (id, col = null) => {
    setActiveView(id);
    setSelectedCollection(col);
    if (setIsOpen) setIsOpen(false);
  };

  // Cálculo do total de cenas (excluindo referências da Conta C)
  const totalCenas = packs.filter(p => p.storage_account !== 'C').length;

  // Acesso ao Plugin Editor: só quem comprou o plugin ou curso
  const hasPluginAccess =
    user?.has_plugin === true ||
    user?.has_course === true ||
    user?.course_override === true;

  const SectionHeader = ({ children }) => (
    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest px-3 mb-2 mt-2">
      {children}
    </p>
  );

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[180] md:hidden" onClick={() => setIsOpen(false)} />}
      
      <aside className={cn(
        "fixed left-0 top-0 h-screen w-[260px] bg-[#0D0D0D] border-r border-[#1A1A1A] flex flex-col z-[200] transition-transform md:translate-x-0 font-['Helvetica',_sans-serif]",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        
        <div className="p-5 border-b border-[#1A1A1A] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0056D2] rounded-lg flex items-center justify-center shadow-lg shadow-[#0056D2]/20">
              <Video className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm font-bold text-white tracking-tight uppercase">Editor Premium</span>
          </div>
          <button onClick={() => setIsOpen(false)} className="md:hidden text-gray-500"><X size={20} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 space-y-1 px-3">

          {/* ==================== BIBLIOTECA ==================== */}
          <SectionHeader>Biblioteca</SectionHeader>

          <button
            onClick={() => handleNavigation('home')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
              activeView === 'home' && !selectedCollection ? "bg-[#0056D2] text-white" : "text-gray-400 hover:text-white"
            )}
          >
            <Home size={18} /> <span>Início</span>
          </button>

          <button
            onClick={() => handleNavigation('references')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold text-[#FFD700]"
          >
            <Lightbulb size={18} className="text-[#FFD700]" />
            <span>Ref Premium</span>
          </button>

          <button
            onClick={() => handleNavigation('sounds')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
              activeView === 'sounds'
                ? "bg-[#0056D2] text-white"
                : "text-gray-400 hover:text-white"
            )}
          >
            <Music size={18} />
            <span>Músicas Virais</span>
          </button>

          <button
            onClick={() => handleNavigation('downloads')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
              activeView === 'downloads' ? "bg-[#0056D2] text-white" : "text-gray-400 hover:text-white"
            )}
          >
            <Download size={18} /> <span>Minhas Cenas</span>
          </button>

          <button
            onClick={() => handleNavigation('favorites')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all group",
              activeView === 'favorites'
                ? "bg-gradient-to-r from-[#0056D2] to-[#0066FF] text-white shadow-lg shadow-[#0056D2]/30"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            )}
          >
            <Heart
              size={18}
              className={cn(
                "transition-all",
                activeView === 'favorites' ? "fill-current" : "group-hover:fill-current"
              )}
            />
            <div className="flex items-center justify-between w-full">
              <span>Favoritos</span>
              {favorites.length > 0 && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF3B5C] text-white">
                  {favorites.length}
                </span>
              )}
            </div>
          </button>

          {/* ==================== ÁUDIO & EFEITOS ==================== */}
          {hasSFXPlan && (
            <>
              <SectionHeader>Áudio &amp; Efeitos</SectionHeader>

              <button
                onClick={() => handleNavigation('sfx')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
                  activeView === 'sfx'
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                    : "text-gray-400 hover:text-white"
                )}
              >
                <Wand2 size={18} />
                <div className="flex items-center justify-between w-full">
                  <span>SFX</span>
                  {!hasSFXAccess && (
                    <span className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">
                      <Lock size={9} /> {sfxDaysRemaining}d
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => handleNavigation('overlays')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
                  activeView === 'overlays'
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                    : "text-gray-400 hover:text-white"
                )}
              >
                <Layers size={18} />
                <div className="flex items-center justify-between w-full">
                  <span>Overlays</span>
                  {hasSFXAccess ? (
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 uppercase tracking-widest">
                      NOVO
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">
                      <Lock size={9} /> {sfxDaysRemaining}d
                    </span>
                  )}
                </div>
              </button>
            </>
          )}

          {/* ==================== MEU PLUGIN (quem comprou) ==================== */}
          {hasPluginAccess && (
            <>
              <SectionHeader>Meu Plugin</SectionHeader>

              <button
                onClick={() => handleNavigation('plugin')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all group",
                  activeView === 'plugin'
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30"
                    : "bg-gradient-to-r from-emerald-600/10 to-teal-600/10 text-emerald-400 hover:from-emerald-600/20 hover:to-teal-600/20 hover:text-emerald-300 border border-emerald-500/20"
                )}
              >
                <Package
                  size={18}
                  className={cn(
                    activeView === 'plugin' ? "" : "group-hover:scale-110 transition-transform"
                  )}
                />
                <div className="flex items-center justify-between w-full">
                  <span>Baixar Plugin</span>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase tracking-widest">
                    INCLUSO
                  </span>
                </div>
              </button>
            </>
          )}

          {/* ==================== EXTRAS PARA VOCÊ ==================== */}
          <SectionHeader>Extras para você</SectionHeader>

          {!hasPluginAccess && (
            <button
              onClick={() => handleNavigation('plugin')}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
                activeView === 'plugin'
                  ? "bg-[#0056D2] text-white"
                  : "text-gray-400 hover:text-white"
              )}
            >
              <Package size={18} />
              <div className="flex items-center justify-between w-full">
                <span className="truncate">Plugin Editor</span>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-orange-500/15 text-orange-300 uppercase tracking-widest">
                  UPGRADE
                </span>
              </div>
            </button>
          )}

          <button
            onClick={() => handleNavigation('course')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-all",
              activeView === 'course'
                ? "bg-[#0056D2] text-white"
                : "text-gray-400 hover:text-white"
            )}
          >
            <GraduationCap size={18} />
            <div className="flex items-center justify-between w-full">
              <span className="truncate">Plugin de Legendas</span>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-orange-500/15 text-orange-300 uppercase tracking-widest">
                NOVO
              </span>
            </div>
          </button>

          {/* ==================== COLEÇÕES ==================== */}
          <div className="my-6 border-t border-white/5 mx-2" />
          <SectionHeader>Coleções</SectionHeader>

          {collections.map((col) => (
            <button
              key={col.name}
              onClick={() => handleNavigation('collection', col.name)}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold transition-all",
                selectedCollection === col.name ? "text-[#0056D2] bg-[#0056D2]/5" : "text-gray-400 hover:text-white"
              )}
            >
              <span className="truncate">{col.name}</span>
              <span className="text-[10px] opacity-50">{col.count}</span>
            </button>
          ))}
        </nav>

        {/* RODAPÉ ATUALIZADO COM CONTADOR */}
        <div className="p-4 border-t border-[#1A1A1A] bg-[#0A0A0A]">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Film size={14} className="text-[#0056D2]" />
            <span className="text-[11px] font-bold text-gray-400">
              <span className="text-white">{totalCenas}</span> cenas disponíveis
            </span>
          </div>
          
          <div className="flex items-center justify-between bg-white/[0.03] p-2 rounded-lg border border-white/5">
            <div className="flex flex-col min-w-0">
              <span className="text-[10px] font-bold text-gray-200 truncate">
                {user?.email?.split('@')[0]}
              </span>
              <span className="text-[9px] text-[#0056D2] font-bold uppercase">Membro Premium</span>
            </div>
            <button onClick={onLogout} className="text-gray-500 hover:text-red-500 transition-all p-1">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
